<template>
  <div class="page">
    <!-- スマホ前提なので RLS の報告画面と同じくヘッダー無し。上に緑のボタン、その下に絞り込みの帯 -->
    <div class="top-row">
      <PrimaryButton text="メニューへ" size="sm" class="go-btn bg-green-700 hover:bg-green-800" :buttonContents="'要'" @click="$router.push({ name: 'Launcher' })">
        <Icon iconName="Home" slot="before" class="w-5 h-5 mr-1" />
      </PrimaryButton>
      <!-- 進捗（全員の未完了。近くに未予定の物件が無いか見るとき） -->
      <PrimaryButton text="進捗へ" size="sm" class="go-btn bg-green-700 hover:bg-green-800" :buttonContents="'要'" @click="goSchedule()">
        <Icon iconName="ViewGrid" slot="before" class="w-5 h-5 mr-1" />
      </PrimaryButton>
      <!-- 地図（ピン＝今出ている物件と現在地。近い物件から回る用）。件数の隣 -->
      <button type="button" class="map-btn ml-auto" :class="isMap ? 'map-btn-active' : ''" @click="toggleMap()">
        <Icon iconName="LocationMarker" class="w-5 h-5 mr-1" />
        {{ isMap ? '地図を閉じる' : '地図' }}
      </button>
      <!-- 地図を開いている間だけ：現在地とピンを取り直して描き直す -->
      <button v-if="isMap" type="button" class="map-btn" :disabled="loading" @click="refreshMap()">
        <Icon iconName="Refresh" class="w-5 h-5 mr-1" :class="{ 'animate-spin': loading }" />
        更新
      </button>
      <div class="font-bold text-gray-700 whitespace-nowrap">{{ total }} 件</div>
    </div>

    <div class="bar">
      <!-- 期間（今日／明日まで／1週間。今月末まで）。遅れと実施中は期間に関係なく必ず出る -->
      <select v-model="quick" class="filter-input quick-select has-value rounded border-gray-300 text-sm" title="期間" @change="setQuick(quick)">
        <option value="today">今日</option>
        <option value="tomorrow">明日まで</option>
        <option value="week">1週間</option>
      </select>
      <!-- 担当者は最初は自分。変えて他の人の分も見られる（空白を除いた部分一致・「未定」で担当者が空の行） -->
      <!-- 何か入っている欄は黄色（絞り込みに気づかず「出ない」とならないように） -->
      <input v-model="filter.staffName" type="search" placeholder="担当者 検索" class="filter-input w-40 rounded border border-gray-300 px-2 text-sm" :class="filter.staffName.trim() ? 'has-value' : ''" title="「未定」で担当者が空の行" @keydown.enter="search()" />
      <input v-model="filter.siteName" type="search" placeholder="物件名 検索" class="filter-input w-56 rounded border border-gray-300 px-2 text-sm" :class="filter.siteName.trim() ? 'has-value' : ''" @keydown.enter="search()" />
      <!-- 未完了／完了／全て（常に絞り込んでいるので常に黄色） -->
      <select v-model="filter.show" class="filter-input quick-select has-value rounded border-gray-300 text-sm" title="状態" @change="search()">
        <option value="notDone">未完了</option>
        <option value="done">完了</option>
        <option value="all">全て</option>
      </select>
      <PrimaryButton text="検索" class="search-btn justify-center ml-auto" @click="search()">
        <Icon v-if="loading" iconName="Refresh" slot="before" class="w-4 h-4 mr-1 animate-spin" />
      </PrimaryButton>
    </div>

    <main class="px-1 pb-4">
      <div class="flex flex-col">
        <div class="rounded-md border border-gray-200 bg-white">
          <!-- 地図（MAP を押したとき。表の上。緯度経度が取れない物件はピン無し） -->
          <div v-if="isMap" class="qr-map-wrap">
            <div ref="map" class="qr-map"></div>
            <div class="qr-map-legend">
              <span><i class="dot" style="background:#dc2626"></i>遅れ</span>
              <span><i class="dot" style="background:#2563eb"></i>予定（日付）</span>
              <span><i class="dot" style="background:#d97706"></i>実施中</span>
              <span><i class="dot" style="background:#16a34a"></i>完了</span>
              <span><i class="dot dot-me"></i>現在地</span>
              <span class="text-gray-500">ピンを押すと実施できます</span>
              <span v-if="noPinCount" class="text-red-600">住所から位置が取れない物件 {{ noPinCount }} 件</span>
            </div>
          </div>

          <div v-if="loading" class="flex py-12 items-center justify-center font-bold text-gray-400">
            Searching...
          </div>

          <div v-else-if="!list.length" class="flex py-12 items-center justify-center font-bold text-gray-400">
            対象データがありません。
          </div>

          <template v-else>
            <!-- ========== PC：表（進捗と同じ見た目。予定日・担当者はここでは変えない） ========== -->
            <table class="w-full border-collapse text-sm list-table">
              <thead class="sticky top-0 bg-gray-100 z-10">
                <tr class="border-b border-gray-300 text-left text-xs text-gray-600">
                  <th class="px-3 py-2">物件</th>
                  <th class="px-3 py-2">前回完了日</th>
                  <th class="px-3 py-2">予定日</th>
                  <th class="px-3 py-2">担当者</th>
                  <th class="px-3 py-2 text-center">完了</th>
                  <th class="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody class="text-gray-700">
                <!-- 完了した行は行ごと押して報告書を開く -->
                <tr v-for="row in pagedList" :key="row.id" class="border-b border-gray-200" :class="[rowClass(row), isDone(row) ? 'done-row' : '']" @click="isDone(row) && openForm(row)">
                  <td class="px-3 py-2">
                    <div class="font-bold">{{ row.siteName }}</div>
                    <div class="text-xs text-gray-500">
                      {{ row.siteAddress }}
                      <a v-if="row.siteAddress" class="map-link" @click.stop="openGoogleMap(row)">地図</a>
                    </div>
                    <div class="text-xs text-gray-500">{{ clientName(row) }}</div>
                  </td>
                  <!-- 前回完了（この月より前で一番新しい完了日。間隔の目安） -->
                  <td class="px-3 py-2 whitespace-nowrap text-gray-500">{{ fmtDate(row.prevCompletionDate) }}</td>
                  <td class="px-3 py-2 whitespace-nowrap">
                    <b :class="isLate(row) ? 'text-red-600' : ''">{{ fmtDate(row.scheduledDate) }}</b>
                    <span v-if="isLate(row)" class="chip chip-late ml-1">遅れ</span>
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap">{{ row.staffName || '未定' }}</td>
                  <!-- 完了：完了日・時間帯（実施中は開始時間だけ） -->
                  <td class="px-3 py-2 whitespace-nowrap text-center">
                    <template v-if="isDone(row)">
                      <span class="font-bold">{{ fmtDate(row.completionDate) }}</span>
                      <span class="text-xs text-gray-500 ml-1">{{ row.startTime }}〜{{ row.endTime || '' }}</span>
                    </template>
                    <span v-else-if="row.startTime" class="text-xs text-gray-500">{{ row.startTime }}〜</span>
                    <span v-else>―</span>
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap text-center">
                    <!-- マスタの合計撮影枚数が無ければボタンを出さず赤字（終了できないため） -->
                    <template v-if="!isDone(row)">
                      <div v-if="needMaster(row)" class="text-xs font-bold text-red-600 whitespace-normal">マスタで「合計撮影枚数」を要設定</div>
                      <PrimaryButton v-else :text="startText(row)" size="sm" class="start-btn w-24" :class="startClass(row)" :disabled="!canStart(row)" @click="openForm(row)" />
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- ========== スマホ：カード ========== -->
            <div class="cards">
              <!-- 完了したカードはカードごと押して報告書を開く -->
              <div v-for="row in pagedList" :key="'c' + row.id" class="card" :class="[cardClass(row), isDone(row) ? 'done-row' : '']" @click="isDone(row) && openForm(row)">
                <div class="flex items-start gap-2">
                  <div class="min-w-0 flex-1">
                    <div class="font-bold text-base leading-tight">{{ row.siteName }}</div>
                    <div class="text-xs text-gray-500 mt-0.5">
                      {{ row.siteAddress }}
                      <a v-if="row.siteAddress" class="map-link" @click.stop="openGoogleMap(row)">地図</a>
                    </div>
                    <div class="text-xs text-gray-500">{{ clientName(row) }}</div>
                  </div>
                  <span v-if="statusChip(row)" class="chip" :class="statusChip(row).cls">{{ statusChip(row).text }}</span>
                </div>
                <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 mt-2">
                  <span class="text-gray-500">前回完了日 {{ fmtDate(row.prevCompletionDate) }}</span>
                  <span>
                    予定日
                    <b :class="isLate(row) ? 'text-red-600' : ''">{{ fmtDate(row.scheduledDate) }}</b>
                    <span v-if="isLate(row)" class="chip chip-late ml-1">遅れ</span>
                  </span>
                  <span>担当 <b>{{ row.staffName || '未定' }}</b></span>
                  <span v-if="row.startTime">{{ row.startTime }}〜{{ row.endTime || '' }}</span>
                  <span v-if="isDone(row)">完了日 <b>{{ fmtDate(row.completionDate) }}</b></span>
                </div>
                <div v-if="!isDone(row)" class="card-btns mt-3" @click.stop>
                  <div v-if="needMaster(row)" class="text-sm font-bold text-red-600 self-center">マスタで「合計撮影枚数」を要設定</div>
                  <PrimaryButton v-else :text="startText(row)" size="lg" class="start-btn card-btn" :class="startClass(row)" :disabled="!canStart(row)" @click="openForm(row)" />
                </div>
              </div>
            </div>
          </template>
        </div>

        <div v-if="total > perPage" class="my-2 w-fit mx-auto">
          <pagination id="pagenation" v-model="page" :per-page="perPage" :records="total" class="text-center" />
        </div>
      </div>
    </main>

    <!-- ピンを押したとき：その物件の予定と「実施する」 -->
    <div v-if="pinRow" class="pin-dialog-bg" @click.self="pinRow = null">
      <div class="pin-dialog">
        <div class="font-bold text-base">{{ pinRow.siteName }}</div>
        <div class="text-xs text-gray-500">
          {{ pinRow.siteAddress }}
          <a v-if="pinRow.siteAddress" class="map-link" @click="openGoogleMap(pinRow)">地図</a>
        </div>
        <!-- 前回完了日は 1 行で、その下に予定日・担当 -->
        <div class="text-sm text-gray-500 mt-3">前回完了日 {{ fmtDate(pinRow.prevCompletionDate) }}</div>
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mt-1">
          <span v-if="statusChip(pinRow)" class="chip" :class="statusChip(pinRow).cls">{{ statusChip(pinRow).text }}</span>
          <span>
            予定日 <b :class="isLate(pinRow) ? 'text-red-600' : ''">{{ fmtDate(pinRow.scheduledDate) }}</b>
            <span v-if="isLate(pinRow)" class="chip chip-late ml-1">遅れ</span>
          </span>
          <span>担当 <b>{{ pinRow.staffName || '未定' }}</b></span>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <WhiteButton text="閉じる" @click="pinRow = null" />
          <span v-if="!isDone(pinRow) && needMaster(pinRow)" class="text-sm font-bold text-red-600 self-center">マスタで「合計撮影枚数」を要設定</span>
          <PrimaryButton v-else-if="!isDone(pinRow)" :text="startText(pinRow)" class="start-btn w-32 justify-center" :class="startClass(pinRow)" :disabled="!canStart(pinRow)" @click="openForm(pinRow)" />
          <SecondaryButton v-else text="開く" class="w-32 justify-center" @click="openForm(pinRow)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import SecondaryButton from '@components/SecondaryButton.vue'
import WhiteButton from '@components/WhiteButton.vue'
import Pagination from 'vue-pagination-2'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import * as utils from '@libs/utils'
import moment from 'moment'
import PageModel from '../../main/pages/PageModel.vue'

const DOW_NAME = ['日', '月', '火', '水', '木', '金', '土']

// 地図（google.maps は public/index.html で読み込み済み）。リアクティブにすると重いので data に入れない
let map = null
let markers = []
let myMarker = null
// ピンの色（遅れ・予定・実施中・完了）
const PIN_COLOR = { late: '#dc2626', plan: '#2563eb', doing: '#d97706', done: '#16a34a' }

export default {
  extends: PageModel,
  components: {
    Icon,
    PrimaryButton,
    SecondaryButton,
    WhiteButton,
    Pagination
  },

  data() {
    const today = moment().format('YYYY-MM-DD')
    return {
      loading: false,
      // 期間（ボタンで決まる。初期値は今日だけ）。quick は押したボタン
      range: { from: today, to: today },
      quick: 'today',
      // 担当者の初期値はログインしている本人（変えてよい）
      filter: {
        staffName: utils.deleteKana(this.$store.getters.user.user_name || ''),
        siteName: '',
        // 未完了（notDone）／完了（done）／全て（all）
        show: 'notDone'
      },
      list: [],
      // ページ（地図が無いときは SQL の LIMIT で 1 ページ分だけ取る。total＝絞り込み後の全件数）
      page: 1,
      perPage: 20,
      total: 0,
      // 地図。最初は出さない（MAP ボタンで出す）。noPinCount＝位置が取れずピンにできなかった物件の数
      isMap: false,
      noPinCount: 0,
      // 現在地（1 回取れたら持っておく）
      myPos: null,
      // ピンを押したときのダイアログ
      pinRow: null
    }
  },

  computed: {
    today() {
      return moment().format('YYYY-MM-DD')
    },

    // 表に出す行。地図のときは全行を持っているので画面で分け、そうでなければ list が 1 ページ分
    pagedList() {
      if (!this.isMap) {
        return this.list
      }
      const start = (this.page - 1) * this.perPage
      return this.list.slice(start, start + this.perPage)
    }
  },

  watch: {
    // ページ送り（地図が無いときは 1 ページ分だけ取っているので取り直す。地図のときは画面で分けるだけ）
    page() {
      if (!this.isMap) {
        this.fetchList()
      }
    }
  },

  async created() {
    await this.search()
  },

  beforeDestroy() {
    this.clearMarkers()
    map = null
  },

  methods: {
    /**
     * 期間のボタン（今日／明日まで／1週間）→ 期間を入れて検索
     * 翌月は出さない（翌月の予定はまだ組み直されるし、開くと翌月の行が作られてしまう）ので、終了日は今月末まで
     */
    async setQuick(kind) {
      const today = moment()
      this.quick = kind
      this.range.from = today.format('YYYY-MM-DD')
      let to = today.clone()
      if (kind === 'tomorrow') {
        to = today.clone().add(1, 'days')
      } else if (kind === 'week') {
        to = today.clone().add(6, 'days')
      }
      const endOfMonth = today.clone().endOf('month')
      if (to.isAfter(endOfMonth)) {
        to = endOfMonth
        await dialogs.showInfoDialog('期間', `翌月の予定は出ません。今月末（${endOfMonth.format('M/D')}）までを表示します。`)
      }
      this.range.to = to.format('YYYY-MM-DD')
      await this.search()
    },

    /**
     * 1 ページ目から取る
     */
    async search() {
      // 1 ページ目に戻す。地図が無いときはページの watch が取りに行くので、ここでは戻すだけ
      if (this.page !== 1 && !this.isMap) {
        this.page = 1
        return
      }
      this.page = 1
      await this.fetchList()
    },

    /**
     * 期間内の予定（検索欄の担当者の分）。実施中と遅れは期間に関係なく出て先頭
     * API: qualityReport/list
     */
    async fetchList() {
      this.loading = true
      try {
        const res = await backend.searchData('qualityReport/list', {
          dateFrom: this.range.from,
          dateTo: this.range.to,
          withLate: 1,
          notDone: this.filter.show === 'done' ? 0 : 1,
          done: this.filter.show === 'notDone' ? 0 : 1,
          siteName: this.filter.siteName.trim(),
          staffName: this.filter.staffName.trim(),
          status: '',
          doingFirst: 1,
          isMap: this.isMap ? 1 : 0,
          page: this.isMap ? '' : this.page,
          perPage: this.perPage,
          userId: this.$store.getters.user.id
        })
        this.list = (res.data && res.data.data) || []
        this.total = this.list.length ? Number(this.list[0].totalCount) : 0
      } catch (e) {
        console.log(e)
        this.list = []
        this.total = 0
        await dialogs.showErrorDialog('エラー', '一覧の取得に失敗しました。')
      }
      this.loading = false
      if (this.isMap) {
        await this.$nextTick()
        await this.initMap(true)
      }
    },

    /**
     * 地図の表示／非表示（地図あり＝全行、なし＝1 ページ分なので取り直す）
     */
    async toggleMap() {
      this.isMap = !this.isMap
      if (!this.isMap) {
        this.clearMarkers()
        map = null
      }
      // 開くたびに現在地を取り直す（移動してから開き直しても前の場所のままにならないように）
      this.myPos = null
      await this.search()
    },

    /**
     * 地図の更新ボタン：現在地とピンを取り直し、現在地とピンが入る範囲に合わせ直す
     */
    async refreshMap() {
      this.myPos = null
      // map を捨てると描き直しのとき「今の中心を保つ」にならず、範囲を合わせ直す
      this.clearMarkers()
      map = null
      await this.search()
    },

    /**
     * 地図を描く（ピン＝今出ている物件、青い丸＝現在地。keep＝今の拡大と中心を保つ）
     */
    async initMap(keep) {
      if (!this.$refs.map || typeof google === 'undefined') {
        return
      }
      const rows = this.list.filter((r) => r.latitude && r.longitude)
      this.noPinCount = this.list.length - rows.length
      if (!this.myPos) {
        this.myPos = await this.getMyPos()
      }
      // 地図が消えていたら（取っている間に MAP を閉じた）何もしない
      if (!this.$refs.map) {
        return
      }
      this.clearMarkers()
      const keepView = keep && map
      map = new google.maps.Map(this.$refs.map, {
        zoom: keepView ? map.getZoom() : 12,
        center: keepView ? map.getCenter() : (this.myPos || { lat: 35.624315, lng: 139.579387 }),
        // 店や学校のラベルは消す（物件のピンを見やすく）
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }]
      })
      markers = rows.map((row) => {
        const marker = new google.maps.Marker({
          map,
          position: { lat: Number(row.latitude), lng: Number(row.longitude) },
          title: row.siteName,
          icon: this.pinIcon(row),
          optimized: false,
          zIndex: this.isDone(row) ? 1 : 2
        })
        marker.rowId = row.id
        marker.addListener('click', () => this.openPin(row))
        return marker
      })
      if (this.myPos) {
        myMarker = new google.maps.Marker({
          map,
          position: this.myPos,
          title: '現在地',
          icon: this.myIcon(),
          optimized: false,
          zIndex: 3
        })
      }
      // 最初は現在地とピンが全部入るように
      if (!keepView) {
        const points = rows.map((r) => ({ lat: Number(r.latitude), lng: Number(r.longitude) }))
        if (this.myPos) {
          points.push(this.myPos)
        }
        if (points.length >= 2) {
          const bounds = new google.maps.LatLngBounds()
          points.forEach((p) => bounds.extend(p))
          map.fitBounds(bounds)
        } else if (points.length === 1) {
          map.setCenter(points[0])
          map.setZoom(14)
        }
      }
    },

    /**
     * 現在地（取れなければ null。PC は Wi-Fi などからの推定なのでずれることがある）
     */
    async getMyPos() {
      try {
        const pos = await utils.getLocation2()
        if (pos && pos.lat && pos.lng) {
          return { lat: pos.lat, lng: pos.lng }
        }
      } catch (e) {
        console.log(e)
      }
      return null
    },

    /**
     * ピンを消す
     */
    clearMarkers() {
      markers.forEach((m) => m.setMap(null))
      markers = []
      if (myMarker) {
        myMarker.setMap(null)
        myMarker = null
      }
    },

    /**
     * ピンの絵（色は状態、文字は予定日 M/D）
     */
    pinIcon(row) {
      const st = Number(row.status)
      const color = st >= 3 ? PIN_COLOR.done : st === 2 ? PIN_COLOR.doing : this.isLate(row) ? PIN_COLOR.late : PIN_COLOR.plan
      const text = row.scheduledDate ? moment(row.scheduledDate).format('M/D') : '未定'
      // 日付は吹き出しの中に大きく（丸の中だと地図の文字と重なって読めない）。下の点が物件の位置
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="68" height="62" viewBox="0 0 68 62">
        <rect x="3" y="3" width="62" height="32" rx="9" fill="#fff" stroke="${color}" stroke-width="3"/>
        <path d="M25 35 L43 35 L34 48 Z" fill="${color}"/>
        <circle cx="34" cy="54" r="5" fill="${color}" stroke="#fff" stroke-width="2"/>
        <text x="34" y="25" text-anchor="middle" font-size="16" font-weight="bold" font-family="sans-serif" fill="${color}">${text}</text>
      </svg>`
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new google.maps.Size(68, 62),
        anchor: new google.maps.Point(34, 54)
      }
    },

    /**
     * 現在地の絵（青い丸）
     */
    myIcon() {
      // 大きめの青い丸＋「現在地」の札（物件の吹き出しに紛れないように）
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="72" height="78" viewBox="0 0 72 78">
        <circle cx="36" cy="36" r="34" fill="#3b82f6" fill-opacity="0.22"/>
        <circle cx="36" cy="36" r="20" fill="#3b82f6" fill-opacity="0.35"/>
        <circle cx="36" cy="36" r="11" fill="#2563eb" stroke="#fff" stroke-width="3"/>
        <rect x="14" y="58" width="44" height="18" rx="9" fill="#2563eb"/>
        <text x="36" y="71" text-anchor="middle" font-size="12" font-weight="bold" font-family="sans-serif" fill="#fff">現在地</text>
      </svg>`
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: new google.maps.Size(72, 78),
        anchor: new google.maps.Point(36, 36)
      }
    },

    /**
     * ピンを押した → その物件のダイアログ
     */
    openPin(row) {
      this.pinRow = row
    },

    fmtDate(d) {
      if (!d) {
        return '―'
      }
      const m = moment(d)
      return `${m.format('M/D')}（${DOW_NAME[m.day()]}）`
    },

    isDone(row) {
      return Number(row.status) >= 3
    },

    /**
     * 遅れ＝予定日を過ぎて未完了（実施中は除く）
     */
    isLate(row) {
      return Number(row.status) < 2 && !!row.scheduledDate && row.scheduledDate < this.today
    },

    /**
     * 実施中・完了だけチップを出す（未実施は予定日とボタンで分かる）
     */
    statusChip(row) {
      const st = Number(row.status)
      if (st >= 3) {
        return { cls: 'chip-done', text: '完了' }
      }
      if (st === 2) {
        return { cls: 'chip-doing', text: '実施中' }
      }
      return null
    },

    /**
     * 行の色（実施中は黄色、今日の予定は薄い緑）
     */
    rowClass(row) {
      if (Number(row.status) === 2) {
        return 'row-doing'
      }
      if (!this.isDone(row) && row.scheduledDate === this.today) {
        return 'row-today'
      }
      return this.isDone(row) ? 'bg-gray-50' : ''
    },

    /**
     * カードの色（実施中は黄色の枠、今日の予定は緑の枠）
     */
    cardClass(row) {
      if (Number(row.status) === 2) {
        return 'card-doing'
      }
      if (!this.isDone(row) && row.scheduledDate === this.today) {
        return 'card-today'
      }
      return ''
    },

    /**
     * 得意先名（1 と 2 をつなげる）
     */
    clientName(row) {
      return [row.clientName1, row.clientName2].filter((v) => v).join(' ')
    },

    /**
     * 「実施する」ボタンの文字（実施中は「実施中」）
     */
    startText(row) {
      return Number(row.status) === 2 ? '実施中' : '実施する'
    },

    /**
     * 「実施する」ボタンの色（実施中はオレンジ、それ以外は緑＝画面を移るボタン）
     */
    startClass(row) {
      // この Tailwind には orange が無いので yellow-600（#d97706＝実施中のピンと同じ色）
      return Number(row.status) === 2 ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'
    },

    /**
     * 実施できるか（未完了は予定日が必要）
     */
    canStart(row) {
      return Number(row.status) >= 2 || !!row.scheduledDate
    },

    /**
     * 実施する前にマスタの設定が要るか（合計撮影枚数が無いと終了できず、実施中はマスタも変えられないので、実施前に止める）
     */
    needMaster(row) {
      return Number(row.status) < 2 && !row.totalPhoto
    },

    /**
     * 報告書画面へ（未完了は実施、完了は見るだけ）
     */
    async openForm(row) {
      if (this.needMaster(row)) {
        await dialogs.showErrorDialog('実施', 'マスタで「合計撮影枚数」を設定してから実施してください。')
        return
      }
      if (!this.canStart(row)) {
        await dialogs.showErrorDialog('実施', '予定日を入れてから実施してください。')
        return
      }
      // 実施前で、前回の完了から 14 日経っていなければ確認（止めない）
      if (Number(row.status) < 2 && row.prevCompletionDate) {
        const days = moment().startOf('day').diff(moment(row.prevCompletionDate), 'days')
        if (days < 14) {
          const r = await dialogs.showConfirmDialog('実施', `前回の完了（${moment(row.prevCompletionDate).format('M/D')}）から ${days} 日しか経っていません。よろしいですか？`, 'はい', 'キャンセル')
          if (r !== 'YES') {
            return
          }
        }
      }
      this.$router.push({ name: 'QualityReportForm', query: { id: String(row.id) } })
    },

    /**
     * 住所で Google マップを別タブに（経路案内はそちらで）
     */
    openGoogleMap(row) {
      utils.GoogleMap(row.siteAddress)
    },

    /**
     * 進捗（稼働）へ。全員の未完了が出る
     */
    goSchedule() {
      this.$router.push({ name: 'QualityReportSchedule' })
    }
  }
}
</script>

<style scoped>
/* PC で横に伸びすぎないよう幅を抑えて中央に（スマホは画面いっぱい） */
.page {
  max-width: 820px;
  margin: 0 auto;
}

/* 上のボタンの行。下の帯のボタンと押し間違えないよう間を空ける */
.top-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 6px 4px 14px;
}

/* 絞り込みの帯（RLS の報告画面と同じ濃い青緑） */
.bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  margin: 0 4px 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #0d9488;
}

.filter-input {
  height: 38px;
  box-sizing: border-box;
}

/* 絞り込みが入っている欄は黄色（RlsForm と同じ） */
.has-value {
  background-color: yellow;
  font-weight: bold;
}

.search-btn {
  height: 38px;
  min-width: 5.5rem;
}

/* 期間のドロップダウン */
.quick-select {
  width: 7.5rem;
}

/* 地図の切り替え（丸いピル。OFF＝白地に青、ON＝青地に白。黄色は検索欄の印に使うので使わない） */
.map-btn {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 16px 0 12px;
  border: 2px solid #2563eb;
  border-radius: 9999px;
  background: #fff;
  color: #2563eb;
  font-weight: bold;
  font-size: 14px;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.map-btn-active {
  background: #2563eb;
  color: #fff;
}

/* 緑のボタンは同じ幅・地図ボタンと同じ高さ */
.go-btn {
  width: 8.5rem;
  height: 38px;
  justify-content: center;
  white-space: nowrap;
}

/* 地図は表の上。凡例はその下に 1 行 */
.qr-map {
  height: 420px;
}

.qr-map-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  padding: 6px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 12px;
  font-weight: bold;
  color: #374151;
}

.qr-map-legend .dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  margin-right: 4px;
}

.qr-map-legend .dot-me {
  background: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

/* ピンを押したときのダイアログ */
.pin-dialog-bg {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.pin-dialog {
  background: #fff;
  border-radius: 10px;
  padding: 18px 20px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

/* 表の行の色（実施中・今日の予定） */
.row-doing {
  background: #fffbeb;
}

.row-today {
  background: #f0fdf4;
}

/* カード（スマホ）。PC では表を出すので隠す */
.cards {
  display: none;
  grid-template-columns: 1fr;
  gap: 10px;
  padding: 10px;
}

.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
}

/* 完了した行・カードは押して報告書を開く。乗せたときの水色は定期清掃の完了一覧と同じ（Tailwind の背景色が !important なので、こちらも !important） */
.done-row {
  cursor: pointer;
}

.done-row:hover {
  background: rgba(163, 231, 228, 0.67) !important;
}

.card-doing {
  border-color: #f59e0b;
  background: #fffbeb;
}

.card-today {
  border-color: #16a34a;
}

.card-btns {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.card-btn {
  min-height: 44px;
  flex: 1 1 0;
  max-width: 180px;
  justify-content: center;
}

/* 「実施する」は予定日が無いと押せない＝グレー（共通ボタンの無効色は indigo なので上書き） */
.start-btn:disabled {
  background-color: #9ca3af !important;
  box-shadow: none;
}

.map-link {
  margin-left: 6px;
  color: #2563eb;
  cursor: pointer;
  white-space: nowrap;
}

.map-link:hover {
  text-decoration: underline;
}

.chip {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 1px 9px;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
}

.chip-doing {
  background: #fef3c7;
  color: #b45309;
}

.chip-done {
  background: #d1fae5;
  color: #047857;
}

.chip-late {
  background: #fee2e2;
  color: #b91c1c;
}

@media screen and (max-width: 820px) {
  /* スマホは表の代わりにカード */
  .list-table {
    display: none;
  }

  .cards {
    display: grid;
  }

  .qr-map {
    height: 300px;
  }

  /* 検索欄は横いっぱいに分ける。入力の文字は 16px（iOS で拡大されない） */
  .bar .filter-input {
    flex: 1 1 140px;
    min-width: 0;
    width: auto;
    font-size: 16px;
    height: 44px;
  }

  /* 指で押すので 44px 以上・間を広く。上の行と帯の間はさらに空ける */
  .map-btn,
  .search-btn,
  .top-row .go-btn {
    height: 44px;
  }

  /* 期間のドロップダウンは横に伸ばさない */
  .bar .quick-select {
    flex: 0 0 auto;
    width: 8rem;
  }

  .top-row {
    gap: 14px;
    margin-bottom: 22px;
  }

  .top-row .go-btn {
    padding-left: 16px;
    padding-right: 16px;
  }

  .bar {
    gap: 12px 14px;
    padding: 12px 10px;
  }
}

@media screen and (min-width: 600px) and (max-width: 820px) {
  .cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
